import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { ContextType } from '@lit/context';
import { Task } from '@lit/task';

import { FlakinessReport } from '@flakiness/flakiness-report';
import { Stats } from '@flakiness/server/common/stats/stats.js';
import { createFakeReportCommit } from '@flakiness/server/common/wireReport.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { api } from './api.js';
import { AttachmentUrlBuilder } from './artifact.js';
import { linkStyles, pageStyles } from './components/cssstyles.js';
import { FKHeadSelectEvent } from './components/head-selector.js';
import { ProjectAlerts } from './components/project-alerts.js';
import { ProjectContext } from './components/project-context.js';
import { consume, contexts } from './contexts.js';
import { FeatureReportViewerState } from './features/feature-report-viewer.js';
import { Linkifier } from './linkifier.js';
import { PageProjectTests } from './page-project-tests.js';
import { REPORT_VIEWER_PAGE_METADATA } from './pageMetadata.js';
import { docsUrl } from './platformUrl.js';
import { RouteConfig, URLState } from './router.js';
import { tasks } from './tasks.js';
import { URLOptions } from './urlOptions.js';

const pageSchema = {
  query: URLOptions.query,
  historyHead: URLState.option<string|undefined>({
    name: 'hhead',
    default: undefined,
    encode: head => head,
    decode: head => head,
  }),
  testTimeline: URLOptions.testTimeline,
  testAttempt: URLOptions.testAttempt,
  plane: URLOptions.plane,
  timelineSplit: URLOptions.timelineSplit,
  page: URLOptions.page,
  pageSize: URLOptions.pageSize,
  sortName: URLOptions.sortName,
  sortDirection: URLOptions.sortDirection,
  historyType: URLOptions.historyType,

  // --- local report state ---

  port: URLState.option<number|undefined>({
    name: 'port',
    default: undefined,
    encode: number => String(number),
    decode: text => text && !isNaN(parseInt(text, 10)) ? parseInt(text, 10) : undefined,
  }),
  token: URLState.option<string|undefined>({
    name: 'token',
    default: undefined,
    encode: token => String(token),
    decode: token => token.trim().length > 0 ? token : undefined,
  }),

  // --- remote report URL ---

  url: URLState.option<string|undefined>({
    name: 'url',
    default: undefined,
    encode: url => url,
    decode: url => url.trim().length > 0 ? url : undefined,
  }),
};

@customElement('page-report-viewer-content')
class PageReportViewerContent extends LitElement {
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;
  @consume({ context: contexts.projectReferences, subscribe: true }) private _projectReferences?: ContextType<typeof contexts.projectReferences>;
  // This page renders inside <app-body>, not <project-body>, so it renders its own alerts.
  @consume({ context: contexts.serverInfo, subscribe: true }) private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  @property({ attribute: false }) report?: FlakinessReport.Report;
  @property({ attribute: false }) testId?: Stats.TestId;
  @property({ attribute: false }) historyHead?: string;
  @property({ attribute: false }) attachmentURLBuilder?: AttachmentUrlBuilder;
  @property({ attribute: false }) state?: FeatureReportViewerState;
  @property({ attribute: false }) linkifier?: Linkifier<FeatureReportViewerState>;

  private _commitTask = new Task(this, {
    args: () => [this._project, this._snapshotId, this.report] as const,
    task: async ([project, snapshotId, report], { signal }) => {
      CHECK(report);
      let commit: WireTypes.Commit|undefined;
      if (project && snapshotId) {
        const page = await api.project.listCommits.GET({
          orgSlug: project.org.orgSlug,
          projectSlug: project.projectSlug,
          snapshotId,
          commitOptions: {
            head: report.commitId,
            maxCount: 1,
          },
          pageOptions: { number: 0, size: 1 },
        }, { signal });
        // The report may refer to a commit that is not in the project's history.
        commit = page.elements[0];
      }
      return commit ?? createFakeReportCommit(report);
    }
  });

  private _historyHeadTask = tasks.headRef(this, {
    project: () => this._project,
    snapshotId: () => this._snapshotId,
    defaultBranch: () => this._projectReferences?.defaultBranch,
    head: () => {
      if (this.historyHead)
        return this.historyHead;
      if (!this._commitTask.value?.isFakeCommit)
        return this._commitTask.value?.commitId;
    },
  });

  override render() {
    const project = this._project;
    const orgSlug = project?.org.orgSlug;
    const projectSlug = project?.projectSlug;

    return html`
      <report-viewer-header>
        ${orgSlug && projectSlug ? html`
          <h-box>
            <span>History from</span>
            <head-selector size=small .head=${this._historyHeadTask.value}></head-selector>
            <a-ext href=${PageProjectTests.url({ orgSlug, projectSlug, snapshotId: this._snapshotId })}>${orgSlug}/${projectSlug}</a-ext>
          </h-box>
        ` : html`
          <div>
            <h-box>
              <a-ext class=cloud-history-hint href=${docsUrl('/getting-started/')}>Configure History</a-ext>
            </h-box>
          </div>
        `}
      </report-viewer-header>

      <app-body wide>
        ${ProjectAlerts.maybeRender(project, this._snapshotId, this._serverInfo)}
        ${this.report ? html`
          <feature-report-viewer
            .historyHead=${this._historyHeadTask.value?.commit.commitId}
            .report=${this.report}
            .commit=${this._commitTask.value}
            .attachmentURLBuilder=${this.attachmentURLBuilder}
            .state=${this.state}
            .linkifier=${this.linkifier}
          ></feature-report-viewer>
        ` : nothing}
      </app-body>
      <app-footer></app-footer>
    `;
  }

  static styles = [linkStyles, pageStyles, css`
    .cloud-history-hint {
      color: var(--sl-color-neutral-400);
    }
  `]
}

@customElement('page-report-viewer')
export class PageReportViewer extends LitElement {
  static routes(): RouteConfig[] {
    return [{
      path: [
        '/viewer',
        '/viewer/',
        '/viewer/test/:testId',
        '/viewer/test/:testId/',
      ],
      render: (groups) => html`
        <page-report-viewer .testId=${groups?.testId}></page-report-viewer>
      `,
    }];
  }

  static url(options: FeatureReportViewerState & {
    historyHead?: string,
    port?: number,
    token?: string,
    url?: string,
  }) {
    const pathname = options.testId ? ['', 'viewer', 'test', options.testId].join('/') : '/viewer';
    const linker = URLState.createLinkRenderer(pageSchema, { pathname });
    return linker({
      port: options.port,
      token: options.token,
      url: options.url,
      query: options.query?.trim() ? options.query : undefined,
      plane: options.plane,
      timelineSplit: options.timelineSplit,
      testAttempt: options.testAttempt,
      historyHead: options.historyHead,
      page: options.page,
      pageSize: options.pageSize,
      sortDirection: options.sortDirection,
      sortName: options.sortName,
      testTimeline: options.testTimeline,
      historyType: options.historyType,
    });
  }

  private _urlState = new URLState(this, pageSchema);

  @property({ attribute: false }) testId?: Stats.TestId;

  private _reportUrl(options: {
    port?: number,
    token?: string,
    url?: string,
  }) {
    if (options.url)
      return options.url;
    if (options.token && options.port)
      return `http://127.0.0.1:${options.port}/${options.token}/report.json`;
    return undefined;
  }

  private _reportTask = new Task(this, {
    args: () => [this._urlState.state.port, this._urlState.state.token, this._urlState.state.url] as const,
    task: async ([port, token, url], { signal }) => {
      const reportUrl = this._reportUrl({ port, token, url });
      CHECK(reportUrl);
      let errorText: string|undefined;
      try {
        const res = await fetch(reportUrl, { signal });
        if (res.status >= 200 && res.status < 300)
          return (await res.json()) as FlakinessReport.Report;
        errorText = `Server returned ${res.status} when loading report`;
      } catch (e) {
        console.error(e);
      }
      throw errorText ?? `Failed to load report`;
    }
  });

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this._reportTask.value] as const,
    metadata: ([report]) => ({
      ...REPORT_VIEWER_PAGE_METADATA,
      title: report?.title ? `${report.title} · Report Viewer` : 'Report Viewer',
    }),
  });

  private _attachmentUrlBuilderTask = new Task(this, {
    args: () => [this._urlState.state.port, this._urlState.state.token, this._urlState.state.url] as const,
    task: async ([port, token, url], { signal }) => {
      const reportUrl = this._reportUrl({ port, token, url });
      CHECK(reportUrl);
      const base = new URL(reportUrl);
      if (base.pathname.endsWith('/report.json'))
        base.pathname = base.pathname.substring(0, base.pathname.lastIndexOf('/'));
      return ((attachmentId: string, isPlaywrightTrace?: boolean) => {
        const url = new URL(base);
        url.pathname += `/attachments/${attachmentId}`;
        if (isPlaywrightTrace)
          return `https://trace.playwright.dev?trace=${encodeURIComponent(url.toString())}`;
        return url.toString();
      }) satisfies AttachmentUrlBuilder;
    }
  });

  override render() {
    if (!this._urlState.state.url && (!this._urlState.state.port || !this._urlState.state.token))
      return nothing;
    return this._reportTask.render({
      pending: () => html`
        <report-viewer-header></report-viewer-header>
        <app-body wide><report-viewer-stub></report-viewer-stub></app-body>
        <app-footer></app-footer>
      `,
      error: (err: any) => html`
        <report-viewer-header></report-viewer-header>
        <app-body wide><h3>Error loading report: ${err}</h3></app-body>
        <app-footer></app-footer>
      `,
      complete: report => {
        const [orgSlug, projectSlug] = report.flakinessProject?.split('/') ?? [];
        return ProjectContext.render({ orgSlug, projectSlug }, html`
          <page-report-viewer-content
            .report=${report}
            .testId=${this.testId}
            .historyHead=${this._urlState.state.historyHead}
            .attachmentURLBuilder=${this._attachmentUrlBuilderTask.value}
            .state=${{
              timelineSplit: this._urlState.state.timelineSplit,
              page: this._urlState.state.page,
              pageSize: this._urlState.state.pageSize,
              plane: this._urlState.state.plane,
              query: this._urlState.state.query,
              sortDirection: this._urlState.state.sortDirection,
              sortName: this._urlState.state.sortName,
              testAttempt: this._urlState.state.testAttempt,
              testId: this.testId,
              testTimeline: this._urlState.state.testTimeline,
              historyType: this._urlState.state.historyType,
            } satisfies FeatureReportViewerState}
            .linkifier=${(options: FeatureReportViewerState) => PageReportViewer.url({
              ...options,
              port: this._urlState.state.port,
              token: this._urlState.state.token,
              url: this._urlState.state.url,
              historyHead: this._urlState.state.historyHead,
            })}
            @fk-select=${(event: FKHeadSelectEvent) => this._urlState.update({ historyHead: event.head.name })}
          ></page-report-viewer-content>
        `);
      },
    });
  }

  static styles = [linkStyles, pageStyles]
}
